import React from 'react';

const AUTO_LINK_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION_PATTERN = /[.,!?;:]$/;
const TRAILING_QUOTES_PATTERN = /[)"'\]}]+$/;
const SKIP_LINKIFY_SELECTOR = 'a, code, pre, script, style, textarea';

function countChar(value = '', char = '') {
  if (!char) return 0;
  return Array.from(String(value || '')).filter((entry) => entry === char).length;
}

function splitTrailingText(candidate = '') {
  let linkText = String(candidate || '');
  let trailing = '';

  while (TRAILING_PUNCTUATION_PATTERN.test(linkText)) {
    trailing = linkText.slice(-1) + trailing;
    linkText = linkText.slice(0, -1);
  }

  let changed = true;
  while (changed && TRAILING_QUOTES_PATTERN.test(linkText)) {
    changed = false;
    const last = linkText.slice(-1);
    const pairs = {
      ')': '(',
      ']': '[',
      '}': '{',
    };
    if (!pairs[last] || countChar(linkText, last) > countChar(linkText, pairs[last])) {
      trailing = last + trailing;
      linkText = linkText.slice(0, -1);
      changed = true;
    }
  }

  return { linkText, trailing };
}

export function getAutoLinkHref(value = '') {
  const candidate = String(value || '').trim();
  if (!candidate) return '';
  const normalized = /^www\./i.test(candidate) ? `https://${candidate}` : candidate;

  try {
    const parsed = new URL(normalized);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.href;
  } catch {
    return '';
  }
}

export function linkifyTextParts(text = '') {
  const source = String(text || '');
  const parts = [];
  let lastIndex = 0;

  source.replace(AUTO_LINK_PATTERN, (match, offset) => {
    const previous = offset > 0 ? source[offset - 1] : '';
    if (previous && /[\w@]/.test(previous)) return match;

    const { linkText, trailing } = splitTrailingText(match);
    const href = getAutoLinkHref(linkText);
    if (!href) return match;

    if (offset > lastIndex) parts.push(source.slice(lastIndex, offset));
    parts.push({ type: 'link', text: linkText, href });
    if (trailing) parts.push(trailing);
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < source.length) parts.push(source.slice(lastIndex));
  return parts.length ? parts : [source];
}

export function renderLinkifiedText(text = '') {
  return linkifyTextParts(text).map((part, index) => {
    if (!part || typeof part === 'string') return part;
    return (
      <a
        key={`${part.href}-${index}`}
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {part.text}
      </a>
    );
  });
}

function shouldSkipTextNode(node) {
  if (!node?.nodeValue || !AUTO_LINK_PATTERN.test(node.nodeValue)) {
    AUTO_LINK_PATTERN.lastIndex = 0;
    return true;
  }
  AUTO_LINK_PATTERN.lastIndex = 0;

  let parent = node.parentElement;
  while (parent) {
    if (typeof parent.matches === 'function' && parent.matches(SKIP_LINKIFY_SELECTOR)) return true;
    parent = parent.parentElement;
  }
  return false;
}

export function linkifyHtmlText(input = '') {
  const html = String(input || '');
  if (!html || typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const view = template.ownerDocument?.defaultView || window;
  const nodeFilter = view.NodeFilter;
  if (!nodeFilter || typeof template.content.querySelectorAll !== 'function') return html;

  const walker = template.ownerDocument.createTreeWalker(
    template.content,
    nodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        return shouldSkipTextNode(node) ? nodeFilter.FILTER_REJECT : nodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const nodes = [];
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }

  nodes.forEach((textNode) => {
    const fragment = template.ownerDocument.createDocumentFragment();
    linkifyTextParts(textNode.nodeValue).forEach((part) => {
      if (!part || typeof part === 'string') {
        fragment.appendChild(template.ownerDocument.createTextNode(part || ''));
        return;
      }
      const anchor = template.ownerDocument.createElement('a');
      anchor.href = part.href;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.textContent = part.text;
      fragment.appendChild(anchor);
    });
    textNode.replaceWith(fragment);
  });

  return template.innerHTML;
}
